import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Loader2, ArrowLeft } from "lucide-react";
import { CompanyNav } from "./CompanyNav";
import type { CompanyPortalConfig } from "@/config/companies";

interface CompanyProfileProps {
  config: CompanyPortalConfig;
}

export function CompanyProfile({ config }: CompanyProfileProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [fullName, setFullName] = useState("");

  useEffect(() => {
    const loadProfile = async () => {
      if (!user) return;
      
      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("full_name")
          .eq("id", user.id)
          .single();

        if (error) throw error;
        
        if (data) {
          setFullName(data.full_name || "");
        }
      } catch (error) {
        console.error("Error loading profile:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, [user]);

  const getUserInitials = () => {
    if (fullName) {
      return fullName
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .substring(0, 2);
    }
    if (user?.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return "U";
  };

  return (
    <div className="min-h-screen bg-background">
      <CompanyNav config={config} />
      
      <div className="container max-w-2xl mx-auto pt-24 px-6 pb-12">
        <Card>
          <CardHeader>
            <CardTitle>My Profile</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="space-y-6">
                {/* Back Button */}
                <Button
                  variant="ghost"
                  onClick={() => navigate(`/${config.slug}-app`)}
                  className="mb-4"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back to Dashboard
                </Button>

                {/* Avatar Section */}
                <div className="flex justify-center">
                  <Avatar className="h-24 w-24">
                    <AvatarImage src={user?.user_metadata?.avatar_url} />
                    <AvatarFallback 
                      className="text-2xl"
                      style={{
                        background: `linear-gradient(135deg, ${config.branding.gradientStart}, ${config.branding.gradientEnd})`,
                        color: "white"
                      }}
                    >
                      {getUserInitials()}
                    </AvatarFallback>
                  </Avatar>
                </div>

                {/* Email (Read-only) */}
                <div className="space-y-2">
                  <Label>Email</Label>
                  <p className="text-sm text-foreground bg-muted px-3 py-2 rounded-md">
                    {user?.email || "N/A"}
                  </p>
                </div>

                {/* Full Name (Read-only) */}
                <div className="space-y-2">
                  <Label>Full Name</Label>
                  <p className="text-sm text-foreground bg-muted px-3 py-2 rounded-md">
                    {fullName || "Not provided"}
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
